const API_BASE = import.meta.env.VITE_API_URL || '';

// App config
export interface AppConfig {
  itsmWebUrl: string;
}

// Cache for config
let cachedConfig: AppConfig | null = null;

export interface RequestDisplayItem {
  id: number;
  subject: string;
  status: string;
  category: string | null;
  subcategory: string | null;
  impact: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  requestedBy: string | null;
  requestedFor: string | null;
  member: string | null;
  team: string | null;
  resolutionTargetAt: string | null;
}

export interface RequestStats {
  needsResponse: number;
  inProgress: number;
  waitingCustomer: number;
  waitingOther: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function getInitData(): string {
  return window.Telegram?.WebApp?.initData || '';
}

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': getInitData(),
    },
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  return response.json();
}

// Settings types
export interface UserSettings {
  receiveNotifications: boolean;
  reminderInterval: number;
  quietHoursEnabled: boolean;
  quietHoursStart: number;
  quietHoursEnd: number;
  notifyNeedsResponse: boolean;
  notifyInProgress: boolean;
  notifyWaitingCustomer: boolean;
}

// Admin types
export interface AdminMe {
  isAdmin: boolean;
}

export interface InviteCode {
  code: string;
  expiresAt: string;
  createdBy: number;
  used: boolean;
}

export interface AdminUser {
  chatId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  active: boolean;
  receiveNotifications: boolean;
  lastActivity: string | null;
}

export interface CleanupRequestsResult {
  deletedRequests: number;
  deletedHistory: number;
}

export interface CleanupHistoryResult {
  deleted: number;
}

export interface MemberStat {
  member: string;
  count: number;
}

export interface RequestBrief {
  id: number;
  subject: string;
  completedAt?: string;
}

// History types
export interface HistoryChange {
  field: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface HistoryEntry {
  id: number;
  action: 'create' | 'update' | 'info';
  createdAt: string;
  createdBy: string | null;
  changes: HistoryChange[];
}

export interface RequestHistoryResponse {
  requestId: number;
  subject: string;
  currentStatus: string;
  entries: HistoryEntry[];
}

// Report types
export interface ReportPeriods {
  currentYear: number;
  currentMonth: number;
  years: number[];
  months: Array<{ value: number; label: string }>;
}

export interface ReportStats {
  total: number;
  completed: number;
  created: number;
  received: number;
}

async function fetchApiWithBody<T>(
  endpoint: string,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  body?: unknown
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': getInitData(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    // Try to extract error message from response body
    let errorMessage = `API Error: ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Failed to parse JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export const api = {
  // Config
  getConfig: async (): Promise<AppConfig> => {
    if (cachedConfig) return cachedConfig;
    const config = await fetchApi<AppConfig>('/api/mini-app/config');
    cachedConfig = config;
    return config;
  },

  // Requests
  getRequests: (params: { page?: number; limit?: number; category?: string; search?: string } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.category) searchParams.set('category', params.category);
    if (params.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<RequestDisplayItem>>(
      `/api/mini-app/requests${query ? `?${query}` : ''}`
    );
  },

  getRequestById: (id: number) => {
    return fetchApi<RequestDisplayItem>(`/api/mini-app/requests/${id}`);
  },

  getRequestHistory: (id: number) => {
    return fetchApi<RequestHistoryResponse>(`/api/mini-app/requests/${id}/history`);
  },

  getStats: () => {
    return fetchApi<RequestStats>('/api/mini-app/stats');
  },

  getRequestsByCategory: (category: string, params: { page?: number; limit?: number } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<RequestDisplayItem>>(
      `/api/mini-app/categories/${category}${query ? `?${query}` : ''}`
    );
  },

  // Settings
  getSettings: () => {
    return fetchApi<UserSettings>('/api/mini-app/settings');
  },

  updateSettings: (settings: Partial<UserSettings>) => {
    return fetchApiWithBody<UserSettings>('/api/mini-app/settings', 'PATCH', settings);
  },

  // Admin
  getAdminMe: () => {
    return fetchApi<AdminMe>('/api/mini-app/admin/me');
  },

  generateInviteCode: () => {
    return fetchApiWithBody<InviteCode>('/api/mini-app/admin/invite-codes', 'POST');
  },

  getInviteCodes: () => {
    return fetchApi<InviteCode[]>('/api/mini-app/admin/invite-codes');
  },

  getAdminUsers: () => {
    return fetchApi<AdminUser[]>('/api/mini-app/admin/users');
  },

  cleanupRequests: (olderThanDays: number) => {
    return fetchApiWithBody<CleanupRequestsResult>('/api/mini-app/admin/cleanup/requests', 'POST', { olderThanDays });
  },

  cleanupHistory: (olderThanDays: number) => {
    return fetchApiWithBody<CleanupHistoryResult>('/api/mini-app/admin/cleanup/history', 'POST', { olderThanDays });
  },

  // Extended Stats
  getMemberStats: () => {
    return fetchApi<MemberStat[]>('/api/mini-app/admin/stats/members');
  },

  getTransferredRequests: () => {
    return fetchApi<PaginatedResponse<RequestBrief>>('/api/mini-app/admin/stats/transferred');
  },

  getCompletedRequests: () => {
    return fetchApi<PaginatedResponse<RequestBrief>>('/api/mini-app/admin/stats/completed');
  },

  // Reports
  getReportPeriods: () => {
    return fetchApi<ReportPeriods>('/api/mini-app/reports/periods');
  },

  getReportDownloadUrl: (year: number, month: number): string => {
    const initData = getInitData();
    // Build URL with auth in query param for direct download
    return `${API_BASE}/api/mini-app/reports/${year}/${month}?initData=${encodeURIComponent(initData)}`;
  },

  downloadReport: async (year: number, month: number): Promise<{ blob: Blob; filename: string; stats: ReportStats }> => {
    const response = await fetch(`${API_BASE}/api/mini-app/reports/${year}/${month}`, {
      headers: {
        'X-Telegram-Init-Data': getInitData(),
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `Failed to generate report: ${response.status}`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `Report_${year}_${month}.xlsx`;

    const statsHeader = response.headers.get('X-Report-Stats');
    const stats: ReportStats = statsHeader ? JSON.parse(statsHeader) : { total: 0, completed: 0, created: 0, received: 0 };

    return { blob, filename, stats };
  },

  // Send report via Telegram bot to user's chat
  sendReport: (year: number, month: number) => {
    return fetchApiWithBody<{ success: boolean; stats: ReportStats }>(
      `/api/mini-app/reports/${year}/${month}/send`,
      'POST'
    );
  },
};

// Extend Window interface for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
          };
        };
        ready: () => void;
        close: () => void;
        expand: () => void;
        MainButton: {
          show: () => void;
          hide: () => void;
          setText: (text: string) => void;
          onClick: (callback: () => void) => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
        };
        openLink: (url: string) => void;
      };
    };
  }
}
